import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DeleteEntityCardDialog } from "@/components/entities/delete-entity-card-dialog";
import { EntityCardForm } from "@/components/entities/entity-card-form";
import { EntityRelationshipPanel } from "@/components/entities/entity-relationship-panel";
import { EntityWorkspaceHeader, type EntityEditorSaveState } from "@/components/entities/entity-workspace-header";
import { EntityWorkspaceRail } from "@/components/entities/entity-workspace-rail";
import { ImageGenerationWorkflowDialogs } from "@/components/image-generation/image-generation-workflow-dialogs";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { applyEntityFilters, getEntityCardHref, getSelectedCardHiddenByFilters } from "@/features/entities";
import {
  buildEntityImageDescriptionParts,
  buildEntityImagePrompt,
  buildImageDraftSessionFromSource,
  mapImageCitation,
} from "@/features/image-generation";
import { useRuntimeCreditCost } from "@/features/pricing/useRuntimePricing";
import { acceptStoryImportElement } from "@/features/story-import/api/storyImportClient";
import { isStoryImportReviewEditState } from "@/features/story-import/review/importReviewEdit";
import { useEntityWorkspaceStore } from "@/stores/entityWorkspaceStore";
import { useProjectImageStore } from "@/stores/project-image-store";
import { projectShellStore } from "@/stores/projectShellStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import {
  buildImportedEntityDraft,
  buildImportedEntityElementData,
} from "@/features/entities/api/entityClient";

const ENTITY_RAIL_COLLAPSED_STORAGE_KEY = "entities:workspace-rail-collapsed";

function readEntityRailCollapsedState() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(ENTITY_RAIL_COLLAPSED_STORAGE_KEY) === "true";
}

export function EntityWorkspacePage() {
  const { t } = useTranslation();
  const imageCreditCost = useRuntimeCreditCost("image_generation");
  const location = useLocation();
  const navigate = useNavigate();
  const { cardId, projectId } = useParams<{ cardId?: string; projectId: string }>();
  const importReviewEditState = useMemo(() => {
    if (
      !projectId
      || !isStoryImportReviewEditState(location.state)
      || location.state.projectId !== projectId
      || (location.state.element.elementType !== "character" && location.state.element.elementType !== "item")
    ) {
      return null;
    }

    return location.state;
  }, [location.state, projectId]);
  const suppressNextHydrateRef = useRef(false);
  const activeCardId = useEntityWorkspaceStore((state) => state.activeCardId);
  const applyGeneratedImage = useEntityWorkspaceStore((state) => state.applyGeneratedImage);
  const cards = useEntityWorkspaceStore((state) => state.cards);
  const clearFilters = useEntityWorkspaceStore((state) => state.clearFilters);
  const clearLimitPrompt = useEntityWorkspaceStore((state) => state.clearLimitPrompt);
  const clearNotice = useEntityWorkspaceStore((state) => state.clearNotice);
  const closeDeleteDialog = useEntityWorkspaceStore((state) => state.closeDeleteDialog);
  const createRelationship = useEntityWorkspaceStore((state) => state.createRelationship);
  const deleteCard = useEntityWorkspaceStore((state) => state.deleteCard);
  const deleteDialog = useEntityWorkspaceStore((state) => state.deleteDialog);
  const deleteRelationship = useEntityWorkspaceStore((state) => state.deleteRelationship);
  const draftType = useEntityWorkspaceStore((state) => state.draftType);
  const filters = useEntityWorkspaceStore((state) => state.filters);
  const hydrateWorkspace = useEntityWorkspaceStore((state) => state.hydrateWorkspace);
  const isRecoveringMedia = useEntityWorkspaceStore((state) => state.isRecoveringMedia);
  const limitPrompt = useEntityWorkspaceStore((state) => state.limitPrompt);
  const markMediaRecoveryRequired = useEntityWorkspaceStore(
    (state) => state.markMediaRecoveryRequired,
  );
  const mediaRecoveryRequired = useEntityWorkspaceStore((state) => state.mediaRecoveryRequired);
  const notice = useEntityWorkspaceStore((state) => state.notice);
  const openCard = useEntityWorkspaceStore((state) => state.openCard);
  const openDeleteDialog = useEntityWorkspaceStore((state) => state.openDeleteDialog);
  const raceSpeciesOptions = useEntityWorkspaceStore((state) => state.raceSpeciesOptions);
  const refreshMediaUrls = useEntityWorkspaceStore((state) => state.refreshMediaUrls);
  const relationshipTargetOptions = useEntityWorkspaceStore((state) => state.relationshipTargetOptions);
  const saveCard = useEntityWorkspaceStore((state) => state.saveCard);
  const saveRelationship = useEntityWorkspaceStore((state) => state.saveRelationship);
  const setSearchText = useEntityWorkspaceStore((state) => state.setSearchText);
  const setTypeFilter = useEntityWorkspaceStore((state) => state.setTypeFilter);
  const startCreateCard = useEntityWorkspaceStore((state) => state.startCreateCard);
  const status = useEntityWorkspaceStore((state) => state.status);
  const unavailableCardId = useEntityWorkspaceStore((state) => state.unavailableCardId);
  const cancelCreateCard = useEntityWorkspaceStore((state) => state.cancelCreateCard);
  const startDraftSession = useProjectImageStore((state) => state.startDraftSession);
  const creditBalance = useSubscriptionStore((state) => state.creditBalance);
  const tier = useSubscriptionStore((state) => state.tier);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<EntityEditorSaveState>("idle");
  const [railCollapsed, setRailCollapsed] = useState(() => readEntityRailCollapsedState());
  const importReviewEntityDraft = useMemo(() => {
    if (!importReviewEditState) {
      return null;
    }

    const { element } = importReviewEditState;
    if (element.elementType !== "character" && element.elementType !== "item") {
      return null;
    }

    return buildImportedEntityDraft({
      displayTitle: element.displayTitle,
      elementData: element.elementData,
      elementType: element.elementType,
    });
  }, [importReviewEditState]);
  const editorDraftType = importReviewEntityDraft?.type ?? draftType;
  const isImportReviewEditingEntity = Boolean(importReviewEditState && importReviewEntityDraft);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    if (suppressNextHydrateRef.current && !cardId) {
      suppressNextHydrateRef.current = false;
      return;
    }

    void hydrateWorkspace(projectId, cardId ?? null);
  }, [cardId, hydrateWorkspace, projectId]);

  useEffect(() => {
    setSaveError(null);
    setSaveState("idle");
  }, [activeCardId, draftType]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(ENTITY_RAIL_COLLAPSED_STORAGE_KEY, String(railCollapsed));
  }, [railCollapsed]);

  const activeCard = useMemo(
    () => cards.find((card) => card.id === activeCardId) ?? null,
    [activeCardId, cards],
  );

  const visibleCards = useMemo(
    () => applyEntityFilters(cards, filters),
    [cards, filters],
  );

  const selectedCardHiddenByFilters = useMemo(
    () =>
      getSelectedCardHiddenByFilters(
        visibleCards,
        activeCardId,
        Boolean(draftType),
      ),
    [activeCardId, draftType, visibleCards],
  );

  const availableCharacterTargets = useMemo(
    () =>
      cards
        .filter((card) => card.type === "character" && card.id !== activeCard?.id)
        .map((card) => ({
          description: card.typeLabel,
          id: card.id,
          name: card.name,
          type: card.type,
        })),
    [activeCard?.id, cards],
  );

  const availableRelationshipTargets = useMemo(
    () =>
      relationshipTargetOptions.filter(
        (option) => option.kind !== "entity-card" || option.id !== activeCard?.id,
      ),
    [activeCard?.id, relationshipTargetOptions],
  );

  const hasRecoverableMedia = useMemo(
    () =>
      mediaRecoveryRequired
      || cards.some((card) => card.hasImage && !card.imageUrl),
    [cards, mediaRecoveryRequired],
  );

  async function handleGenerateEntityImage() {
    if (!projectId || !activeCard) {
      return;
    }

    const projectImageSettings = await projectShellStore
      .getState()
      .hydrateProjectImageSettings(projectId);

    if (!projectImageSettings) {
      return;
    }

    startDraftSession(
      buildImageDraftSessionFromSource({
        creditCost: imageCreditCost,
        creditsRemaining: creditBalance,
        existingImagePath: activeCard.imagePath,
        projectId,
        projectImageSettings,
        prompt: buildEntityImagePrompt({
          descriptionParts: buildEntityImageDescriptionParts(activeCard),
          entityName: activeCard.name,
          styleDescription: projectImageSettings.styleDescription,
          typeLabel: activeCard.typeLabel,
        }),
        sourceCitations: [
          mapImageCitation({
            reason: "Selected entity card for this prompt.",
            sourceKind: "entity_card",
            sourceLabel: activeCard.name,
            sourceRecordId: activeCard.id,
          }),
        ],
        sourceId: activeCard.id,
        sourceKind: "entity_card",
        sourceTitle: activeCard.name,
        tier,
      }),
    );
  }

  if (!projectId) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-base text-muted-foreground">{t("projects.dashboard.missing")}</p>
        </CardContent>
      </Card>
    );
  }

  if (status === "loading" && cards.length === 0) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-base text-muted-foreground">{t("entities.workspace.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  if (status === "error" && cards.length === 0) {
    return (
      <Card>
        <CardContent className="space-y-4 p-8">
          <div className="space-y-2">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-clay">
              {t("entities.workspace.eyebrow")}
            </p>
            <h2 className="text-3xl text-foreground">{t("entities.workspace.unavailableTitle")}</h2>
          </div>
          <p className="max-w-3xl text-base leading-7 text-muted-foreground">
            {notice ? t(notice.message) : t("entities.messages.loadError")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="project-shell-page">
      {notice ? (
        <div className="project-shell-notice" role={notice.kind === "error" ? "alert" : "status"}>
          <div className="flex items-start justify-between gap-3">
            <p>{t(notice.message)}</p>
            <button
              className="text-muted-foreground transition-colors hover:text-foreground"
              onClick={clearNotice}
              type="button"
            >
              {t("projects.actions.dismiss")}
            </button>
          </div>
        </div>
      ) : null}

      {limitPrompt ? (
        <div
          className="rounded-[1.75rem] border border-amber-300/60 bg-amber-50/90 px-5 py-4 text-sm text-amber-950 shadow-vellum"
          role="status"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-semibold">{t("entities.messages.limitReachedTitle")}</p>
              <p>
                {t("entities.messages.limitReachedBody", {
                  allowed: limitPrompt.allowedCount,
                  current: limitPrompt.currentCount,
                  recommendedTier: limitPrompt.recommendedTier,
                  tier: limitPrompt.tier,
                })}
              </p>
            </div>
            <button
              className="text-amber-700 transition-colors hover:text-amber-900"
              onClick={clearLimitPrompt}
              type="button"
            >
              {t("projects.actions.dismiss")}
            </button>
          </div>
        </div>
      ) : null}

      {hasRecoverableMedia ? (
        <div
          className="rounded-[1.75rem] border border-sky-200/70 bg-sky-50/90 px-5 py-4 text-sm text-sky-950 shadow-vellum"
          role="status"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-semibold">{t("entities.messages.mediaRecoveryTitle")}</p>
              <p>{t("entities.messages.mediaRecoveryBody")}</p>
            </div>
            <Button
              disabled={isRecoveringMedia}
              onClick={() => {
                void refreshMediaUrls();
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              {isRecoveringMedia
                ? t("entities.messages.mediaRecoveryLoading")
                : t("entities.messages.mediaRecoveryRetry")}
            </Button>
          </div>
        </div>
      ) : null}

      {importReviewEditState ? (
        <div
          className="rounded-[1.75rem] border border-emerald-200/70 bg-emerald-50/90 px-5 py-4 text-sm text-emerald-950 shadow-vellum"
          role="status"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-semibold">{t("storyImport.actions.editBeforeAccept")}</p>
              <p>{t("storyImport.review.body")}</p>
            </div>
            <Button
              onClick={() => navigate(importReviewEditState.reviewRoute, { replace: true })}
              size="sm"
              type="button"
              variant="outline"
            >
              {t("storyImport.actions.backToImport")}
            </Button>
          </div>
        </div>
      ) : null}

      {unavailableCardId ? (
        <div className="project-shell-notice">
          {t("entities.messages.unavailableCard", { cardId: unavailableCardId })}
        </div>
      ) : null}

      <section
        className={
          importReviewEditState
            ? "grid gap-6"
            : railCollapsed
              ? "grid gap-8 xl:grid-cols-[3rem_minmax(0,1fr)] xl:items-start"
              : "grid gap-8 xl:grid-cols-[20rem_minmax(0,1fr)] xl:items-start"
        }
      >
        {!importReviewEditState ? (
          <EntityWorkspaceRail
            activeEntityId={activeCard?.id ?? null}
            allowCreate={!importReviewEditState}
            cards={visibleCards}
            collapsed={railCollapsed}
            filters={filters}
            isCreatingCard={Boolean(draftType)}
            onClearFilters={() => {
              clearLimitPrompt();
              clearFilters();
            }}
            onCreateCard={(type) => {
              if (cardId) {
                suppressNextHydrateRef.current = true;
              }
              startCreateCard(type);
              navigate(`/projects/${projectId}/entities`);
            }}
            onOpenCard={(entityId) => {
              openCard(entityId);
              navigate(getEntityCardHref(projectId, entityId));
            }}
            onSearchTextChange={setSearchText}
            onToggleCollapsed={() => setRailCollapsed((current) => !current)}
            onTypeFilterChange={setTypeFilter}
            selectedCardHiddenByFilters={selectedCardHiddenByFilters}
            totalCount={cards.length}
          />
        ) : null}

        <div className="min-w-0 space-y-6">
          {!editorDraftType && !activeCard ? (
            <EntityWorkspaceHeader
              card={activeCard}
              draftType={editorDraftType}
              filters={filters}
              resultCount={visibleCards.length}
              saveError={saveError}
              saveState={saveState}
              selectedCardHiddenByFilters={selectedCardHiddenByFilters}
              totalCount={cards.length}
            />
          ) : null}

          {editorDraftType && (draftType || isImportReviewEditingEntity) ? (
            <EntityCardForm
              availableCharacterTargets={availableCharacterTargets}
              availableLoreEntries={raceSpeciesOptions}
              createDraftStorageKey={
                !isImportReviewEditingEntity && editorDraftType
                  ? `${projectId}:${editorDraftType}`
                  : null
              }
              entity={null}
              initialDraft={importReviewEntityDraft?.draft ?? null}
              mode="create"
              onCancelCreate={() => {
                if (importReviewEditState) {
                  navigate(importReviewEditState.reviewRoute, { replace: true });
                  return;
                }

                cancelCreateCard();
                navigate(`/projects/${projectId}/entities`);
              }}
              onSave={async (input) => {
                if (importReviewEditState) {
                  await acceptStoryImportElement({
                    editedElementData: buildImportedEntityElementData(
                      input,
                      importReviewEditState.element.elementData,
                    ),
                    elementId: importReviewEditState.element.id,
                    jobId: importReviewEditState.importJobId,
                  });
                  navigate(importReviewEditState.reviewRoute, { replace: true });
                  return;
                }

                const nextCard = await saveCard(input);
                navigate(getEntityCardHref(projectId, nextCard.id));
                return nextCard;
              }}
              onMediaLoadError={markMediaRecoveryRequired}
              onSaveStateChange={(nextState, nextError) => {
                setSaveState(nextState);
                setSaveError(nextError);
              }}
              type={editorDraftType}
            />
          ) : null}

          {!draftType && activeCard ? (
            <>
              <EntityCardForm
                availableCharacterTargets={availableCharacterTargets}
                availableLoreEntries={raceSpeciesOptions}
                entity={activeCard}
                mode="edit"
                onDelete={() => openDeleteDialog(activeCard.id)}
                onGenerateImage={handleGenerateEntityImage}
                onMediaLoadError={markMediaRecoveryRequired}
                onSave={saveCard}
                onSaveStateChange={(nextState, nextError) => {
                  setSaveState(nextState);
                  setSaveError(nextError);
                }}
                type={activeCard.type}
              />
              <EntityRelationshipPanel
                card={activeCard}
                onCreateRelationship={createRelationship}
                onDeleteRelationship={deleteRelationship}
                onSaveRelationship={saveRelationship}
                targetOptions={availableRelationshipTargets}
              />
            </>
          ) : null}

          {!draftType && !activeCard ? (
            <Card className="project-shell-panel">
              <CardContent className="p-8">
                <p className="text-base text-muted-foreground">
                  {t("entities.workspace.emptySelection")}
                </p>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </section>

      <DeleteEntityCardDialog
        entityTitle={deleteDialog.target?.name ?? ""}
        onConfirm={() => {
          void deleteCard().then(() => {
            navigate(`/projects/${projectId}/entities`);
          });
        }}
        onOpenChange={closeDeleteDialog}
        open={deleteDialog.open}
        submitting={deleteDialog.submitting}
      />
      <ImageGenerationWorkflowDialogs
        onApproved={async (result) => {
          if (activeCard && result.approvedAssetRef) {
            await applyGeneratedImage(
              activeCard.id,
              result.approvedAssetRef,
              result.creditsRemaining ?? null,
            );
          }
        }}
      />
    </div>
  );
}
